import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';

const ACCENT = '#c88346';
const btn = (bg, color = '#fff') => ({ padding: '8px 16px', background: bg, color, border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' });
const inputStyle = { padding: '9px 11px', border: '2px solid rgba(200,131,70,0.18)', borderRadius: '10px', fontSize: '13px', fontWeight: 500, outline: 'none', boxSizing: 'border-box', width: '100%' };
const labelStyle = { fontSize: '11px', fontWeight: 700, color: '#8b7d6e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px', display: 'block' };

// Human-readable basis + pill color (mirrors gstResolver BASIS constants).
const BASIS_LABEL = {
  HSN_9404_BELOW_2500: 'Auto · 9404 ≤ ₹2500',
  HSN_9404_ABOVE_2500: 'Auto · 9404 > ₹2500',
  CH63_BELOW_2500: 'Auto · Ch63 ≤ ₹2500',
  CH63_ABOVE_2500: 'Auto · Ch63 > ₹2500',
  HSN_9403: 'Auto · 9403',
  MANUAL_OVERRIDE: 'Manual override',
  PENDING_REVIEW: 'Pending review',
};
const basisStyle = (b) => {
  if (b === 'MANUAL_OVERRIDE') return { bg: '#fff7ed', fg: '#c2410c' };
  if (b === 'PENDING_REVIEW' || !b) return { bg: '#fef2f2', fg: '#b42318' };
  return { bg: '#ecfdf5', fg: '#047857' }; // auto-resolved
};

// ── Edit drawer ────────────────────────────────────────────────────────────
function EditPanel({ row, onClose, onSaved }) {
  const [hsn, setHsn] = useState(row.hsnCode || '');
  const [mrp, setMrp] = useState(row.mrp != null ? String(row.mrp) : (row.price != null ? String(row.price) : ''));
  const [manual, setManual] = useState(row.basis === 'MANUAL_OVERRIDE');
  const [manualRate, setManualRate] = useState(row.basis === 'MANUAL_OVERRIDE' && row.gstRate != null ? String(row.gstRate) : '');
  const [preview, setPreview] = useState(null); // { ratePercent, basis, autoResolvable }
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Live auto-derivation as HSN/MRP change (same resolver checkout uses).
  useEffect(() => {
    const t = setTimeout(async () => {
      if (!hsn) { setPreview(null); return; }
      try {
        const r = await api.get('/manage/products/preview-rate', { params: { hsn, mrp } });
        setPreview(r.data);
      } catch { setPreview(null); }
    }, 300);
    return () => clearTimeout(t);
  }, [hsn, mrp]);

  const effectiveRate = manual ? (manualRate === '' ? null : Number(manualRate)) : (preview ? preview.ratePercent : null);

  const save = async () => {
    setErr(''); setSaving(true);
    try {
      const r = await api.put(`/manage/products/${encodeURIComponent(row.sku)}`, {
        hsnCode: hsn.trim() || null,
        mrp: mrp === '' ? null : Number(mrp),
        gstRate: manual && manualRate !== '' ? Number(manualRate) : null,
        forceManual: manual,
      });
      onSaved(r.data.row);
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to save');
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(26,26,46,0.35)', zIndex: 100, display: 'flex', justifyContent: 'flex-end' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: '440px', maxWidth: '100%', height: '100%', background: '#fff', boxShadow: '-8px 0 32px rgba(0,0,0,0.12)', padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: ACCENT, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Edit GST / HSN</div>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#3d2b1f', marginTop: '3px', wordBreak: 'break-word' }}>{row.sku}</div>
            {(row.productTitle || row.variantTitle) && <div style={{ fontSize: '12px', color: '#9c8b78', marginTop: '2px' }}>{row.productTitle}{row.variantTitle ? ` · ${row.variantTitle}` : ''}</div>}
          </div>
          <button onClick={onClose} style={{ ...btn('#f1ece5', '#7a6b5d'), padding: '6px 12px' }}>✕</button>
        </div>

        {err && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px', borderRadius: '10px', fontSize: '13px' }}>{err}</div>}

        <div>
          <label style={labelStyle}>HSN Code</label>
          <input value={hsn} onChange={e => setHsn(e.target.value)} placeholder="e.g. 94041000" style={inputStyle} />
          <div style={{ fontSize: '11px', color: '#9c8b78', marginTop: '4px' }}>Digits only. The rate is derived from the HSN chapter/heading + MRP.</div>
        </div>

        <div>
          <label style={labelStyle}>MRP per piece (₹)</label>
          <input value={mrp} onChange={e => setMrp(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder="e.g. 2499" style={inputStyle} />
          <div style={{ fontSize: '11px', color: '#9c8b78', marginTop: '4px' }}>Determines the ≤ ₹2,500 (5%) vs &gt; ₹2,500 (18%) slab for 9404 / Ch63.</div>
        </div>

        {/* Auto-derived rate */}
        <div style={{ background: '#faf7f3', border: '1px solid #ece0d1', borderRadius: '12px', padding: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#8b7d6e', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Auto-derived rate</div>
          {!hsn ? (
            <div style={{ fontSize: '13px', color: '#9c8b78' }}>Enter an HSN to see the rate.</div>
          ) : preview && preview.autoResolvable ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '22px', fontWeight: 800, color: '#047857' }}>{preview.ratePercent}%</span>
              <span style={{ fontSize: '12px', color: '#7a6b5d' }}>{BASIS_LABEL[preview.basis] || preview.basis}</span>
            </div>
          ) : (
            <div style={{ fontSize: '13px', color: '#b42318', fontWeight: 600 }}>
              Can't auto-resolve this HSN{mrp ? '' : ' (an MRP may be needed)'} — set a manual rate below.
            </div>
          )}
        </div>

        {/* Manual override */}
        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#3d2b1f' }}>
            <input type="checkbox" checked={manual} onChange={e => setManual(e.target.checked)} style={{ width: '16px', height: '16px', accentColor: ACCENT }} />
            Set a manual GST rate (override)
          </label>
          {manual && (
            <div style={{ marginTop: '10px' }}>
              <div style={{ position: 'relative', maxWidth: '160px' }}>
                <input value={manualRate} onChange={e => setManualRate(e.target.value.replace(/[^0-9.]/g, ''))} inputMode="decimal" placeholder="e.g. 18" style={{ ...inputStyle, paddingRight: '26px' }} />
                <span style={{ position: 'absolute', right: '11px', top: '50%', transform: 'translateY(-50%)', color: '#9c8b78', fontWeight: 700 }}>%</span>
              </div>
              <div style={{ fontSize: '11px', color: '#c2410c', marginTop: '4px' }}>Stored as MANUAL_OVERRIDE — checkout will use this exact rate and not re-derive it.</div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: '10px', borderTop: '1px solid #f0e8dd' }}>
          <div style={{ fontSize: '12px', color: '#7a6b5d', marginBottom: '10px' }}>
            Will save: HSN <b>{hsn || '—'}</b> · MRP <b>{mrp ? formatCurrency(Number(mrp)) : '—'}</b> · Rate <b>{effectiveRate != null ? `${effectiveRate}%` : '—'}</b>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={onClose} style={{ ...btn('#f1ece5', '#7a6b5d'), flex: 1 }}>Cancel</button>
            <button onClick={save} disabled={saving || (!hsn && effectiveRate == null)} style={{ ...btn(saving ? '#ccc' : `linear-gradient(135deg,${ACCENT} 0%,#b06e2f 100%)`), flex: 2 }}>
              {saving ? 'Saving…' : 'Save GST / HSN'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────
function ManageProducts() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, missing: 0, page: 1, limit: 50 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all'); // all | set | missing | override
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState('');
  const searchTimer = useRef(null);

  const load = useCallback(async (opts = {}) => {
    setLoading(true); setError('');
    try {
      const r = await api.get('/manage/products', { params: { q: query, status, page: opts.page || page, limit: 50 } });
      setRows(r.data.rows || []);
      setMeta({ total: r.data.total || 0, missing: r.data.missing || 0, page: r.data.page || 1, limit: r.data.limit || 50 });
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load products');
    }
    setLoading(false);
  }, [query, status, page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status, page]);
  // Debounced search
  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); load({ page: 1 }); }, 400);
    return () => clearTimeout(searchTimer.current);
    // eslint-disable-next-line
  }, [query]);

  const onSaved = (updated) => {
    setRows(rs => rs.map(r => r.normalizedSku === (updated.sku) || r.sku === updated.sku
      ? { ...r, ...updated } : r));
    setEditing(null);
    setToast(`Saved ${updated.sku}`);
    setTimeout(() => setToast(''), 2200);
  };

  const [exporting, setExporting] = useState(false);
  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await api.get('/manage/products/export', { params: { q: query, status }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `gst-hsn-${status}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError('Export failed');
    }
    setExporting(false);
  };

  // ── Import CSV (bulk HSN / MRP / GST by SKU) ──
  // Accepts the same columns Export produces (SKU, HSN Code, MRP, GST Rate %, Basis; others ignored).
  // Server runs every row through the same rules as the Edit dialog; a dry run is shown first.
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const parseCsv = (text) => {
    const out = []; let row = []; let field = ''; let q = false;
    const s = text.replace(/^﻿/, '');
    for (let i = 0; i < s.length; i++) {
      const c = s[i];
      if (q) {
        if (c === '"') { if (s[i + 1] === '"') { field += '"'; i++; } else q = false; }
        else field += c;
      } else if (c === '"') q = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n' || c === '\r') {
        if (c === '\r' && s[i + 1] === '\n') i++;
        row.push(field); field = '';
        if (row.some(v => v.trim() !== '')) out.push(row);
        row = [];
      } else field += c;
    }
    row.push(field);
    if (row.some(v => v.trim() !== '')) out.push(row);
    return out;
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true); setError('');
    try {
      const table = parseCsv(await file.text());
      if (table.length < 2) throw new Error('The file has no data rows.');
      const head = table[0].map(h => h.trim().toLowerCase());
      const col = (...names) => head.findIndex(h => names.includes(h));
      const iSku = col('sku');
      const iHsn = col('hsn code', 'hsn', 'hsn_code');
      const iMrp = col('mrp', 'mrp_per_piece', 'mrp per piece');
      const iRate = col('gst rate %', 'gst rate', 'gst_rate', 'gst', 'gst %', 'rate');
      const iBasis = col('basis');
      if (iSku < 0) throw new Error('Missing a "SKU" column.');
      if (iHsn < 0 && iMrp < 0 && iRate < 0) throw new Error('Need at least one of: "HSN Code", "MRP", "GST Rate %".');
      const cell = (r, i) => (i >= 0 ? (r[i] ?? '').trim() : '');
      const rows = table.slice(1).map(r => ({
        sku: cell(r, iSku), hsnCode: cell(r, iHsn), mrp: cell(r, iMrp), gstRate: cell(r, iRate), basis: cell(r, iBasis),
      }));

      // 1) Dry run — show what would change.
      const dry = await api.post('/manage/products/import', { rows, dryRun: true }, { timeout: 120000 });
      const s = dry.data.summary;
      const errs = dry.data.errors || [];
      const errLines = errs.slice(0, 5).map(e => `  • line ${e.line}${e.sku ? ` (${e.sku})` : ''}: ${e.error}`).join('\n');
      if (s.created + s.updated === 0) {
        window.alert(`Nothing to import.\n\n${s.unchanged} unchanged, ${s.errors} error(s)${errLines ? `\n${errLines}` : ''}`);
        return;
      }
      const ok = window.confirm(
        `Import GST / HSN from "${file.name}"?\n\n` +
        `New SKUs: ${s.created}\nUpdated SKUs: ${s.updated}\nUnchanged: ${s.unchanged}` +
        `${s.duplicates ? `\nDuplicate rows (last kept): ${s.duplicates}` : ''}\nSkipped (errors): ${s.errors}` +
        `${errLines ? `\n\n${errLines}${errs.length > 5 ? `\n  …and ${errs.length - 5} more` : ''}` : ''}` +
        `\n\nOK = apply these changes.`
      );
      if (!ok) return;

      // 2) Apply.
      const res = await api.post('/manage/products/import', { rows }, { timeout: 300000 });
      const r = res.data.summary;
      setToast(`Imported: ${r.created} new, ${r.updated} updated${r.errors ? `, ${r.errors} skipped` : ''}`);
      setTimeout(() => setToast(''), 4000);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Import failed');
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const totalPages = Math.max(1, Math.ceil(meta.total / meta.limit));
  const filters = [['all', 'All'], ['set', 'GST set'], ['missing', 'Missing'], ['override', 'Manual overrides']];

  return (
    <div style={{ padding: '24px', maxWidth: '1180px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '18px', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#3d2b1f', margin: 0 }}>Manage Products — GST / HSN</h1>
          <div style={{ fontSize: '13px', color: '#9c8b78', marginTop: '4px' }}>
            {meta.total} SKUs{meta.missing > 0 && <> · <span style={{ color: '#b42318', fontWeight: 600 }}>{meta.missing} missing GST</span></>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
            onChange={e => importCsv(e.target.files && e.target.files[0])} />
          <button onClick={() => fileRef.current && fileRef.current.click()} disabled={importing || loading}
            title="Bulk update HSN / MRP / GST rate by SKU from a CSV (same columns as Export)"
            style={{ ...btn('#fff', ACCENT), border: `1px solid ${ACCENT}`, opacity: importing || loading ? 0.6 : 1 }}>
            {importing ? 'Importing…' : '↑ Import CSV'}
          </button>
          <button onClick={exportCsv} disabled={exporting || loading} style={{ ...btn('#fff', ACCENT), border: `1px solid ${ACCENT}`, opacity: exporting || loading ? 0.6 : 1 }}>
            {exporting ? 'Exporting…' : '↓ Export CSV'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px', borderRadius: '10px', marginBottom: '12px', fontSize: '13px' }}>{error}</div>}

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ position: 'relative', flex: '1 1 240px', maxWidth: '380px' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#b9a892' }}>⌕</span>
          <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search SKU, product, or HSN…" style={{ ...inputStyle, paddingLeft: '32px', border: '1px solid #e7ddce' }} />
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {filters.map(([val, label]) => {
            const on = status === val;
            return (
              <button key={val} onClick={() => { setStatus(val); setPage(1); }}
                style={{ padding: '6px 13px', borderRadius: '999px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer',
                  border: on ? `1px solid ${ACCENT}` : '1px solid #e7ddce', background: on ? ACCENT : '#fff', color: on ? '#fff' : '#7a6b5d' }}>
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#faf7f3', borderRadius: '16px', border: '1px solid #ece0d1', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid rgba(200,131,70,0.12)', background: '#fff' }}>
              {['SKU', 'Product', 'HSN', 'MRP', 'GST', 'Basis', ''].map((h, i) => (
                <th key={i} style={{ padding: '11px 12px', textAlign: i >= 3 && i <= 4 ? 'right' : 'left', fontSize: '11px', fontWeight: 700, color: '#8b7d6e', textTransform: 'uppercase', letterSpacing: '0.3px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ padding: '28px', textAlign: 'center', color: '#9c8b78' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: '28px', textAlign: 'center', color: '#9c8b78' }}>No SKUs match.</td></tr>
            ) : rows.map(r => {
              const bs = basisStyle(r.basis);
              return (
                <tr key={r.normalizedSku} style={{ borderBottom: '1px solid #f0e8dd', background: r.hasGst ? 'transparent' : '#fffaf5' }}>
                  <td style={{ padding: '10px 12px', fontSize: '12.5px', fontWeight: 700, color: '#3d2b1f', wordBreak: 'break-word' }}>{r.sku}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#7a6b5d', maxWidth: '260px' }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.productTitle || '—'}</div>
                    {r.variantTitle && <div style={{ fontSize: '11px', color: '#a89a89', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.variantTitle}</div>}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: '12.5px', color: r.hsnCode ? '#3d2b1f' : '#c9beb0', fontFamily: 'ui-monospace, monospace' }}>{r.hsnCode || '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: '12px', color: '#7a6b5d', textAlign: 'right', whiteSpace: 'nowrap' }}>{r.mrp != null ? formatCurrency(r.mrp) : '—'}</td>
                  <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 700, color: r.gstRate != null ? '#047857' : '#c9beb0', textAlign: 'right' }}>{r.gstRate != null ? `${r.gstRate}%` : '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{ background: bs.bg, color: bs.fg, padding: '3px 9px', borderRadius: '7px', fontSize: '11px', fontWeight: 700, whiteSpace: 'nowrap' }}>
                      {BASIS_LABEL[r.basis] || 'Not set'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                    <button onClick={() => setEditing(r)} style={{ ...btn('#fff', ACCENT), border: `1px solid ${ACCENT}`, padding: '5px 14px', fontSize: '12px' }}>Edit</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '16px' }}>
          <button disabled={meta.page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} style={{ ...btn('#fff', ACCENT), border: `1px solid ${ACCENT}`, opacity: meta.page <= 1 ? 0.4 : 1 }}>← Prev</button>
          <span style={{ fontSize: '13px', color: '#7a6b5d' }}>Page {meta.page} of {totalPages}</span>
          <button disabled={meta.page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ ...btn('#fff', ACCENT), border: `1px solid ${ACCENT}`, opacity: meta.page >= totalPages ? 0.4 : 1 }}>Next →</button>
        </div>
      )}

      {editing && <EditPanel row={editing} onClose={() => setEditing(null)} onSaved={onSaved} />}
      {toast && <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: '#065f46', color: '#fff', padding: '10px 20px', borderRadius: '12px', fontSize: '13px', fontWeight: 600, zIndex: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>✓ {toast}</div>}
    </div>
  );
}

export default ManageProducts;
